import { ABTestExperiment } from '@jusbrasil-web/shared-ab-test'
import { createContext, ReactNode, useContext } from 'react'

import { FeatureFlag } from '../../feature-flags'

interface ExperimentsContextInterface {
  getExperiment: (experimentName: string) => ABTestExperiment | undefined
  isParticipating: (experimentName: string, variant: string) => boolean
  getFeatureFlag: (flagName: string) => FeatureFlag | undefined
}

interface ExperimentsProviderProps {
  children: ReactNode
  experiments?: ABTestExperiment[]
  featureFlags?: FeatureFlag[]
}

export const ExperimentsContext = createContext<ExperimentsContextInterface | null>(null)

export function useExperiments() {
  const context = useContext(ExperimentsContext)
  if (!context) {
    throw new Error('useExperiments must be used within an ExperimentsProvider')
  }
  return context
}

export const ExperimentsProvider = (props: ExperimentsProviderProps) => {
  const { children, experiments = [], featureFlags = [] } = props

  const getExperiment = (experimentName: string): ABTestExperiment | undefined =>
    experiments.find((experiment: ABTestExperiment) => experiment?.experiment === experimentName)

  const isParticipating = (experimentName: string, variant: string): boolean => {
    const experiment = getExperiment(experimentName)
    return Boolean(experiment?.participating && experiment?.alternative === variant)
  }

  const getFeatureFlag = (flagName: string): FeatureFlag | undefined =>
    featureFlags.find((flag: FeatureFlag) => flag?.name === flagName)

  return (
    <ExperimentsContext.Provider value={{ getExperiment, isParticipating, getFeatureFlag }}>
      {children}
    </ExperimentsContext.Provider>
  )
}
